import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Search } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

const BAND_STYLE = {
  gotowa: "border-emerald-400 bg-emerald-50 text-emerald-800",
  w_trakcie: "border-amber-400 bg-amber-50 text-amber-800",
  ograniczona: "border-sky-400 bg-sky-50 text-sky-800",
  brak_danych: "border-slate-400 bg-slate-100 text-slate-700",
  nie_wykryto: "border-slate-400 bg-slate-100 text-slate-700",
  zrodlo_niedostepne: "border-dashed border-slate-400 bg-slate-50 text-slate-600",
};
const fmt = (iso) => (iso || "").slice(0, 16).replace("T", " ");

export default function GminaIndexPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const teryt = (slug || "").slice(0, 8);

  useEffect(() => {
    setRec(null);
    api.get(`/index/gminas/${teryt}`).then((r) => setRec(r.data)).catch(() => setRec(false));
  }, [teryt]);

  if (rec === null) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;
  if (rec === false) return <div className="p-20 text-center text-slate-500">404</div>;

  const band = rec.band || "brak_danych";
  const title = `${rec.name} — ${t(`band.short.${band}`)} | PlanGmina`;
  const desc = `Gmina ${rec.name} (${rec.powiat}, woj. ${rec.wojewodztwo}): ${t(`band.short.${band}`)}. ${t("index.subtitle")}`;

  return (
    <div data-testid="gmina-index-page" className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
      </Helmet>

      <Link to="/indeks" data-testid="index-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft size={14} /> {t("index.cta.back")}
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Gmina {rec.name}</h1>
        <span data-testid="official-data-badge" className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
          {t("data.official")}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {t("common.gmina")} {rec.name} · {t("common.powiat")} {rec.powiat} · {t("common.woj")} {rec.wojewodztwo}
        {rec.type ? ` · ${rec.type}` : ""} · <span className="font-mono-data text-xs">{rec.teryt}</span>
      </p>

      <div data-testid="gmina-band-card" className={`mt-6 rounded-xl border-2 p-6 ${BAND_STYLE[band]}`}>
        <p data-testid="gmina-band" className="font-heading text-2xl font-extrabold tracking-tight">{t(`band.${band}`)}</p>
        <p className="mt-1 text-sm font-medium">{t(`band.short.${band}`)}</p>
        <p className="mt-2 text-xs opacity-70">
          {t("index.retrieved")}: {fmt(rec.retrieved_at) || "—"} UTC · {t("index.daily")}
        </p>
      </div>

      <section className="mt-8">
        <h2 className="font-heading text-lg font-semibold">{t("index.why")}</h2>
        <div className="mt-3 overflow-hidden rounded-xl border bg-white">
          <table data-testid="gmina-reasons-table" className="w-full text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wide text-white">
              <tr>
                <th className="px-4 py-3">{t("index.why")}</th>
                <th className="px-4 py-3">{t("report.source")}</th>
                <th className="hidden px-4 py-3 md:table-cell">{t("index.retrieved")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(rec.band_reasons || []).map((r, i) => (
                <tr key={i} data-testid={`gmina-reason-${i}`} className="align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{lang === "pl" ? r.criterion_pl : r.criterion_en}</p>
                    <p className={`mt-0.5 text-xs ${r.source_ok ? "text-slate-600" : "text-amber-700"}`}>
                      {lang === "pl" ? r.value_pl : r.value_en}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {r.source?.url ? (
                      <a href={r.source.url} target="_blank" rel="noreferrer" className="text-[#DC143C] hover:underline">
                        {r.source.authority}
                      </a>
                    ) : r.source?.authority}
                  </td>
                  <td className="hidden px-4 py-3 font-mono-data text-xs text-slate-500 md:table-cell">
                    {fmt(r.source?.retrieved_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {rec.mpzp?.plans?.length > 0 && (
        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">{t("parcel.mpzp")}</h2>
          <ul data-testid="gmina-mpzp-list" className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
            {rec.mpzp.plans.map((p, i) => (
              <li key={i}>
                {p.url ? (
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-[#DC143C] hover:underline">{p.title}</a>
                ) : p.title}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section data-testid="gmina-owner-explainer" className="mt-8 rounded-xl border bg-white p-6">
        <h2 className="font-heading text-lg font-semibold">{t("index.owner.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">{t(`index.owner.${band}`)}</p>
        <button
          data-testid="index-cta-parcel"
          onClick={() => navigate("/")}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#DC143C] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#B91C1C]"
        >
          <Search size={15} /> {t("index.cta.parcel")}
        </button>
      </section>

      <p className="mt-6 text-xs leading-relaxed text-slate-400">{t("index.methodology")}</p>
      <p className="mt-2 text-xs leading-relaxed text-slate-400">{t("disclaimer.text")}</p>
    </div>
  );
}
